import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Seo, breadcrumbSchema, organizationSchema } from "../lib/seo"
import { Arrow, EyebrowLabel, Icon, Reveal, Section } from "../lib/shared"
import { SITE } from "../lib/site"

/* ----------------------------------------------------------------------------
   Project questionnaire.

   The brief, asked in a way somebody will actually finish, for any of the four
   things the studio does rather than websites alone.

   Two fields are required — a name and an email — and every other question can
   be skipped. The form can be sent from any step, because a half-answered
   brief in our inbox is worth more than a complete one abandoned on step three.

   What the middle of the form asks depends on what the visitor picked under
   "What do you need?": a shop owner who wants invoices automated is never
   shown a sitemap question, and someone who picked two services sees both
   sets. That branching lives in one place — NEEDS and the `has()` checks —
   because a question shown to the wrong person is the fastest way to lose them.

   Each answer is paired with its question only at the moment of submitting, so
   the API stores whatever this page asked and rewording a question here needs
   no change anywhere else.
---------------------------------------------------------------------------- */

const API_URL = (import.meta.env?.VITE_API_URL ?? "").replace(/\/$/, "")
const FORM_NAME = "Project questionnaire"
/* Versioned: a draft saved against an older set of questions would restore
   answers that no longer have anywhere to go. */
const DRAFT_KEY = "sableboxx.questionnaire.v3"

/* --- The branching question ---------------------------------------------- */
const SOFTWARE = "Software development"
const AUTOMATION = "AI & automation"
const WEBSITE = "Website & brand"
const SUPPORT = "Support & growth"

const NEEDS = [SOFTWARE, AUTOMATION, WEBSITE, SUPPORT, "Not sure yet"]

/** What each need is called in the pipeline, so a lead lands under a service. */
const LEAD_SERVICE: Record<string, string> = {
  [SOFTWARE]: "Software Development",
  [AUTOMATION]: "AI & Automation",
  [WEBSITE]: "Websites & Brand",
  [SUPPORT]: "Support & Growth",
}

/* --- Options ------------------------------------------------------------- */
const STAGES = [
  "Starting from scratch",
  "Improving something we already have",
  "Not sure yet",
]

/* Outcomes rather than features: these are the words a client uses before they
   know what they are buying, and they apply to all four services. */
const GOALS = [
  "Win more customers",
  "Look more credible",
  "Save staff time",
  "Cut out manual work",
  "Sell online",
  "Serve customers faster",
  "Make decisions from better data",
  "Grow without hiring",
]

/* --- Website & brand ----------------------------------------------------- */
const SITE_TYPES = [
  "Business site",
  "Online store",
  "Portfolio",
  "Booking",
  "Blog",
  "Membership",
  "Events",
  "Non-profit",
]

const PAGES = [
  "Homepage",
  "About",
  "Services",
  "Contact",
  "Blog",
  "Product page",
  "Pricing",
  "Booking / scheduling",
  "Shipping & returns",
  "Search",
  "Custom 404",
]

const SITE_FEATURES = [
  "Online payment",
  "Shopping cart",
  "Booking form",
  "Newsletter signup",
  "Image gallery",
  "Customer login",
  "Live chat",
  "Site search",
  "Maps",
  "Video",
]

const BRAND_GUIDES = [
  "Yes, we have brand guidelines",
  "Not yet, but they're coming",
  "No, and we'd like help with them",
  "Not relevant here",
]

/*
  The original questionnaire asked people to pick one word from each of two
  columns. The same question works better as a position between the two, and
  it is only asked of people who said brand or website — nobody automating
  their invoicing needs an opinion on "luxurious versus natural".
*/
const TONE_PAIRS = [
  ["Greyscale", "Colorful"],
  ["Authoritative", "Friendly"],
  ["Minimalistic", "Complex"],
  ["Luxurious", "Natural"],
] as const

const TONE_STEPS = ["Strongly", "Leaning", "Balanced", "Leaning", "Strongly"]

/* --- Software development ------------------------------------------------ */
const SOFTWARE_KINDS = [
  "Web application",
  "Mobile app",
  "Internal tool",
  "Customer portal",
  "Integration between systems",
  "Data platform",
]

const CAPABILITIES = [
  "Accounts and logins",
  "Payments",
  "Dashboards and reports",
  "Admin panel",
  "Roles and permissions",
  "Notifications",
  "File uploads",
  "Search",
  "Works offline",
  "Multiple locations or branches",
  "Public API",
]

/* --- AI & automation ----------------------------------------------------- */
const WORK_HAPPENS = [
  "WhatsApp",
  "Email",
  "Spreadsheets",
  "Paper or in person",
  "Accounting software",
  "A CRM",
  "Our website",
  "Social media",
]

const AUTOMATIONS = [
  "Answering customer questions",
  "Following up leads",
  "Reading documents or forms",
  "Data entry between systems",
  "Reports and summaries",
  "Booking and scheduling",
  "Invoicing and payments",
  "Stock or inventory",
  "Internal approvals",
  "Onboarding new customers",
]

/* --- Support & growth ---------------------------------------------------- */
const SUPPORT_NEEDS = [
  "Maintenance and updates",
  "Hosting and domains",
  "Fixing what's broken",
  "Speed and performance",
  "Security",
  "SEO",
  "Content and blogging",
  "Analytics and reporting",
  "New features on an existing product",
]

/* --- Anything alongside -------------------------------------------------- */
const EXTRAS = [
  "Logo design",
  "Copywriting",
  "Photography",
  "Custom illustrations",
  "Animations",
  "Training for our team",
]

const TIMEFRAMES = [
  "As soon as possible",
  "1–2 months",
  "3–6 months",
  "Flexible",
]

const BUDGETS = ["Under ₦1m", "₦1m to ₦5m", "₦5m to ₦15m", "₦15m+"]

/* --- State --------------------------------------------------------------- */
type FormState = {
  name: string
  email: string
  business: string
  phone: string
  needs: string[]
  stage: string
  link: string
  proposition: string
  timeframe: string
  budget: string
  goals: string[]
  extras: string[]
  /* Website & brand */
  siteTypes: string[]
  pages: string[]
  siteFeatures: string[]
  dislikes: string
  brandGuides: string
  /** One index per TONE_PAIRS row, 0–4. null while the question is unanswered. */
  tone: (number | null)[]
  /* Software */
  softwareKinds: string[]
  capabilities: string[]
  systems: string
  /* AI & automation */
  manualWork: string
  workHappens: string[]
  automations: string[]
  /* Support & growth */
  supportNeeds: string[]
  problems: string
  /* Everyone */
  customers: string
  success: string
  assets: string
  notes: string
}

const EMPTY: FormState = {
  name: "",
  email: "",
  business: "",
  phone: "",
  needs: [],
  stage: "",
  link: "",
  proposition: "",
  timeframe: "",
  budget: "",
  goals: [],
  extras: [],
  siteTypes: [],
  pages: [],
  siteFeatures: [],
  dislikes: "",
  brandGuides: "",
  tone: TONE_PAIRS.map(() => null),
  softwareKinds: [],
  capabilities: [],
  systems: "",
  manualWork: "",
  workHappens: [],
  automations: [],
  supportNeeds: [],
  problems: "",
  customers: "",
  success: "",
  assets: "",
  notes: "",
}

const STEPS = [
  { title: "The basics", blurb: "Two fields. The rest of this page is optional." },
  { title: "The project", blurb: "Only what fits what you picked. Skip anything you're unsure of." },
  { title: "The details", blurb: "Optional, but it gets us closer on the first try." },
]

export default function Questionnaire() {
  const [form, setForm] = useState<FormState>(EMPTY)
  const [step, setStep] = useState(0)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restored, setRestored] = useState(false)
  const topRef = useRef<HTMLDivElement>(null)
  const honeypot = useRef<HTMLInputElement>(null)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e))
  }

  const toggle = (key: keyof FormState, value: string) => {
    const current = form[key] as string[]
    set(
      key,
      (current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]) as FormState[typeof key],
    )
  }

  /* Restore a draft after hydration, never during it: the prerendered markup
     knows nothing about this browser's localStorage. */
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY)
      if (!raw) return
      const saved = JSON.parse(raw) as Partial<FormState>
      setForm((f) => ({
        ...f,
        ...saved,
        /* Length-matched to the questions on the page today, whatever the
           draft was saved against. */
        tone: TONE_PAIRS.map((_, i) => saved.tone?.[i] ?? null),
      }))
      setRestored(true)
    } catch {
      /* A browser with storage switched off simply gets no draft. */
    }
  }, [])

  useEffect(() => {
    if (sent) return
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form))
    } catch {
      /* Not being able to save the draft is not worth interrupting anyone. */
    }
  }, [form, sent])

  const answers = useMemo(() => buildAnswers(form), [form])

  /** The only two answers we cannot reply without. */
  function validateBasics() {
    const next: Record<string, string> = {}
    if (!form.name.trim()) next.name = "Just your first name is fine."
    if (!form.email.trim()) next.email = "So we have somewhere to reply."
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = "That email address does not look right."
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const canSend = form.name.trim() !== "" && /^\S+@\S+\.\S+$/.test(form.email)

  /** Was this service picked on step one? Decides what steps two and three ask. */
  const has = (need: string) => form.needs.includes(need)

  function go(next: number) {
    setStep(next)
    /* Scrolling to the form, not the top of the document — the step heading
       should be the first thing in view, not the hero again. */
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  function onNext() {
    /* Only the first step can hold anyone up, and only over two fields. */
    if (step === 0 && !validateBasics()) return
    go(Math.min(STEPS.length - 1, step + 1))
  }

  async function submit() {
    if (busy) return
    if (!validateBasics()) {
      go(0)
      return
    }

    if (!API_URL) {
      setError(
        `This form is not connected yet. Please email ${SITE.email} and we will pick it up there.`,
      )
      return
    }

    const payload = {
      form: FORM_NAME,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.business.trim(),
      summary: summarise(form),
      budget: form.budget,
      /* So the lead this opens lands under the right service rather than
         under whatever the form used to assume everyone wanted. */
      service: leadService(form),
      answers,
      /* Honeypot — see the hidden field below. */
      fax: honeypot.current?.value ?? "",
    }

    setBusy(true)
    setError(null)

    let res: Response
    try {
      res = await fetch(`${API_URL}/api/public/submissions`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
    } catch {
      setError(`We could not reach our server. Please email ${SITE.email} instead.`)
      setBusy(false)
      return
    }

    try {
      const body = (await res.json().catch(() => null)) as { error?: string } | null
      if (!res.ok) {
        throw new Error(body?.error ?? "We could not send that. Please try again.")
      }
      try {
        window.localStorage.removeItem(DRAFT_KEY)
      } catch {
        /* nothing to clean up */
      }
      setSent(true)
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Something went wrong. Please email ${SITE.email} instead.`,
      )
    } finally {
      setBusy(false)
    }
  }

  const progress = sent ? 100 : Math.round(((step + 1) / STEPS.length) * 100)
  const last = step === STEPS.length - 1

  return (
    <>
      <Seo
        title="Project Questionnaire"
        description="Tell us about your project: software, AI and automation, a website and brand, or ongoing support. Two required fields, everything else optional, and it saves as you go."
        jsonLd={[
          organizationSchema(),
          breadcrumbSchema([
            { name: "Project Questionnaire", path: "/questionnaire" },
          ]),
        ]}
      />

      <section className="bg-ink text-white">
        <div className="mx-auto max-w-[1280px] px-6 pb-16 pt-40 lg:px-10 lg:pt-48">
          <Reveal>
            <EyebrowLabel light>Project Brief</EyebrowLabel>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 max-w-3xl text-[clamp(2.6rem,4.6vw,3.8rem)] font-black leading-[1.02] tracking-[-0.035em]">
              Tell us about your project.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-xl text-[16px] leading-[1.7] text-neutral-400">
              Software, automation, a website, or keeping what you have
              running. Tell us which and the right questions follow. Only your
              name and email are required. Answer the rest as far as you can
              and we&rsquo;ll fill in the gaps in our reply.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] text-neutral-400">
              <span className="flex items-center gap-2">
                <Icon name="check" className="h-4 w-4 text-accent" />
                About two minutes
              </span>
              <span className="flex items-center gap-2">
                <Icon name="check" className="h-4 w-4 text-accent" />
                Saves as you go
              </span>
              <span className="flex items-center gap-2">
                <Icon name="check" className="h-4 w-4 text-accent" />
                Send it half-finished if you want
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <Section className="bg-paper py-16 lg:py-20">
        <div ref={topRef} className="scroll-mt-28">
          {sent ? (
            <div className="mx-auto max-w-2xl rounded-3xl bg-white p-10 lg:p-14 lg:text-center">
              <span className="grid h-16 w-16 place-items-center rounded-full bg-accent text-white lg:mx-auto">
                <Icon name="credible" className="h-8 w-8" />
              </span>
              <h2 className="mt-6 text-[26px] font-black tracking-[-0.02em]">
                Got it, thank you.
              </h2>
              <p className="mt-3 max-w-md text-[15px] lg:mx-auto leading-relaxed text-neutral-500">
                A real person will read this and reply within two working days.
                If anything is missing we&rsquo;ll ask. You won&rsquo;t need to
                fill in another form.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4 lg:justify-center">
                <Link
                  to="/work"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-[14px] font-medium text-white transition-colors hover:bg-black/85"
                >
                  See our work
                  <Arrow className="h-4 w-4" />
                </Link>
                <a
                  href={`mailto:${SITE.email}`}
                  className="text-[13.5px] text-neutral-500 underline underline-offset-4 hover:text-ink"
                >
                  Add something we missed
                </a>
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl">
              {/* Progress */}
              <div className="flex items-center gap-4">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className="h-full rounded-full bg-accent transition-[width] duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="shrink-0 text-[12px] font-medium uppercase tracking-[0.14em] text-neutral-500">
                  {step + 1} / {STEPS.length}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {STEPS.map((s, i) => (
                  <button
                    key={s.title}
                    type="button"
                    onClick={() => (i === 0 || validateBasics()) && go(i)}
                    aria-current={i === step ? "step" : undefined}
                    className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${
                      i === step
                        ? "bg-ink text-white"
                        : "text-neutral-500 hover:text-ink"
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void submit()
                }}
                onKeyDown={(e) => {
                  /* Enter in a single-line field moves on rather than sending
                     a brief the visitor has not finished looking at. */
                  const target = e.target as HTMLElement
                  if (e.key === "Enter" && target.tagName === "INPUT" && !last) {
                    e.preventDefault()
                    onNext()
                  }
                }}
                className="mt-5 rounded-3xl bg-white p-7 sm:p-9 lg:p-10"
              >
                {/* Honeypot: hidden from people, filled in by bots. */}
                <div
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
                >
                  <label>
                    Fax
                    <input
                      ref={honeypot}
                      type="text"
                      name="fax"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                </div>

                <h2 className="text-[22px] font-black tracking-[-0.02em]">
                  {STEPS[step]!.title}
                </h2>
                <p className="mt-1.5 text-[14px] text-neutral-500">
                  {STEPS[step]!.blurb}
                </p>

                <div className="mt-8 space-y-8">
                  {step === 0 && (
                    <>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <TextField
                          label="Your name"
                          value={form.name}
                          onChange={(v) => set("name", v)}
                          placeholder="Ada"
                          error={errors.name}
                          required
                        />
                        <TextField
                          label="Email"
                          type="email"
                          value={form.email}
                          onChange={(v) => set("email", v)}
                          placeholder="you@company.com"
                          error={errors.email}
                          required
                        />
                        <TextField
                          label="Business name"
                          value={form.business}
                          onChange={(v) => set("business", v)}
                          placeholder="Company Ltd"
                          optional
                        />
                        <TextField
                          label="Phone or WhatsApp"
                          type="tel"
                          value={form.phone}
                          onChange={(v) => set("phone", v)}
                          placeholder="+234 810 000 0000"
                          optional
                        />
                      </div>

                      {/* The one question that decides what the rest of the
                          form asks. */}
                      <ChipGroup
                        legend="What do you need?"
                        hint="Pick as many as apply. The next step follows what you choose."
                        options={NEEDS}
                        selected={form.needs}
                        onToggle={(v) => toggle("needs", v)}
                      />

                      <OptionGroup
                        legend="Is this new, or improving something you have?"
                        options={STAGES}
                        value={form.stage}
                        onChange={(v) => set("stage", v)}
                        inline
                        optional
                      />

                      {form.stage === STAGES[1] && (
                        <TextField
                          label="A link to what you have today"
                          value={form.link}
                          onChange={(v) => set("link", v)}
                          placeholder="company.com, an app store listing, a tool you use…"
                          optional
                        />
                      )}

                      <TextArea
                        label="What does your business do?"
                        value={form.proposition}
                        onChange={(v) => set("proposition", v)}
                        rows={3}
                        placeholder="A sentence is plenty. What would you say to someone who had never heard of you?"
                        optional
                      />

                      <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                        <OptionGroup
                          legend="When do you need it?"
                          options={TIMEFRAMES}
                          value={form.timeframe}
                          onChange={(v) => set("timeframe", v)}
                          inline
                          optional
                        />
                        <OptionGroup
                          legend="Budget range"
                          options={BUDGETS}
                          value={form.budget}
                          onChange={(v) => set("budget", v)}
                          inline
                          optional
                          hint="The fastest way to get a realistic proposal."
                        />
                      </div>
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <ChipGroup
                        legend="What should it achieve?"
                        hint="Describe outcomes rather than features. The features are our job."
                        options={GOALS}
                        selected={form.goals}
                        onToggle={(v) => toggle("goals", v)}
                        initial={6}
                      />

                      {/* Everything below follows what was picked on step one.
                          Someone automating their invoicing never sees a
                          sitemap question. */}
                      {has(WEBSITE) && (
                        <>
                          <SectionLabel>Website &amp; brand</SectionLabel>
                          <ChipGroup
                            legend="What kind of site is it?"
                            options={SITE_TYPES}
                            selected={form.siteTypes}
                            onToggle={(v) => toggle("siteTypes", v)}
                            initial={6}
                          />
                          <ChipGroup
                            legend="Which pages?"
                            hint="A rough idea is fine. We shape the sitemap together later."
                            options={PAGES}
                            selected={form.pages}
                            onToggle={(v) => toggle("pages", v)}
                            initial={6}
                          />
                          <ChipGroup
                            legend="Anything the site has to do?"
                            options={SITE_FEATURES}
                            selected={form.siteFeatures}
                            onToggle={(v) => toggle("siteFeatures", v)}
                            initial={6}
                          />
                        </>
                      )}

                      {has(SOFTWARE) && (
                        <>
                          <SectionLabel>Software</SectionLabel>
                          <ChipGroup
                            legend="What are we building?"
                            options={SOFTWARE_KINDS}
                            selected={form.softwareKinds}
                            onToggle={(v) => toggle("softwareKinds", v)}
                          />
                          <ChipGroup
                            legend="What does it need to do?"
                            options={CAPABILITIES}
                            selected={form.capabilities}
                            onToggle={(v) => toggle("capabilities", v)}
                            initial={6}
                          />
                          <TextArea
                            label="Does it need to work with anything you already use?"
                            value={form.systems}
                            onChange={(v) => set("systems", v)}
                            rows={2}
                            placeholder="Paystack, QuickBooks, a CRM, an existing database, or nothing yet."
                            optional
                          />
                        </>
                      )}

                      {has(AUTOMATION) && (
                        <>
                          <SectionLabel>AI &amp; automation</SectionLabel>
                          <TextArea
                            label="What takes up the most time today?"
                            value={form.manualWork}
                            onChange={(v) => set("manualWork", v)}
                            rows={3}
                            placeholder="The job somebody does by hand every day, week or month. This is the question that matters most here."
                            optional
                          />
                          <ChipGroup
                            legend="Where does that work happen now?"
                            options={WORK_HAPPENS}
                            selected={form.workHappens}
                            onToggle={(v) => toggle("workHappens", v)}
                            initial={6}
                          />
                          <ChipGroup
                            legend="What would you like handled automatically?"
                            options={AUTOMATIONS}
                            selected={form.automations}
                            onToggle={(v) => toggle("automations", v)}
                            initial={6}
                          />
                        </>
                      )}

                      {has(SUPPORT) && (
                        <>
                          <SectionLabel>Support &amp; growth</SectionLabel>
                          <ChipGroup
                            legend="What do you need looked after?"
                            options={SUPPORT_NEEDS}
                            selected={form.supportNeeds}
                            onToggle={(v) => toggle("supportNeeds", v)}
                            initial={6}
                          />
                          <TextArea
                            label="What's going wrong today?"
                            value={form.problems}
                            onChange={(v) => set("problems", v)}
                            rows={3}
                            placeholder="Slow, breaking, nobody to call, no one can update it. Whatever made you look for help."
                            optional
                          />
                        </>
                      )}

                      {form.needs.length === 0 && (
                        <p className="rounded-xl border border-dashed border-neutral-300 px-4 py-4 text-[13.5px] leading-relaxed text-neutral-500">
                          Tell us what you need on{" "}
                          <button
                            type="button"
                            onClick={() => go(0)}
                            className="font-semibold text-ink underline underline-offset-4"
                          >
                            the previous step
                          </button>{" "}
                          and the right questions appear here. Or skip it
                          entirely and we&rsquo;ll ask in our reply.
                        </p>
                      )}

                      <ChipGroup
                        legend="Need anything alongside it?"
                        options={EXTRAS}
                        selected={form.extras}
                        onToggle={(v) => toggle("extras", v)}
                        initial={6}
                      />
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <TextArea
                        label="Who will use it?"
                        value={form.customers}
                        onChange={(v) => set("customers", v)}
                        rows={3}
                        placeholder="Your customers, your team, or both, and what they're trying to get done."
                        optional
                      />
                      <TextArea
                        label="What would make this a success?"
                        value={form.success}
                        onChange={(v) => set("success", v)}
                        rows={3}
                        placeholder="Six months after launch, what is different? A number is ideal, a sentence is fine."
                        optional
                      />

                      {/* Brand questions are only asked of people who said
                          brand or website. */}
                      {has(WEBSITE) && (
                        <>
                          <SectionLabel>Look and feel</SectionLabel>
                          <TextArea
                            label="What do you dislike about your site today?"
                            value={form.dislikes}
                            onChange={(v) => set("dislikes", v)}
                            rows={3}
                            placeholder="Be blunt. This is usually the most useful answer on the page. Skip it if you have no site yet."
                            optional
                          />

                          <fieldset>
                            <legend className="text-[12px] font-medium uppercase tracking-[0.14em] text-neutral-500">
                              How should it feel?
                            </legend>
                            <p className="mt-1.5 text-[13px] text-neutral-500">
                              Tap anywhere on a line. Skip the ones you
                              don&rsquo;t have a view on.
                            </p>
                            <div className="mt-5 space-y-5">
                              {TONE_PAIRS.map(([left, right], i) => (
                                <TonePicker
                                  key={left}
                                  left={left}
                                  right={right}
                                  value={form.tone[i] ?? null}
                                  onChange={(v) => {
                                    const next = [...form.tone]
                                    next[i] = v
                                    set("tone", next)
                                  }}
                                />
                              ))}
                            </div>
                          </fieldset>

                          <OptionGroup
                            legend="Do you have brand guidelines?"
                            options={BRAND_GUIDES}
                            value={form.brandGuides}
                            onChange={(v) => set("brandGuides", v)}
                            optional
                          />
                        </>
                      )}

                      <TextField
                        label="A link to anything that helps"
                        value={form.assets}
                        onChange={(v) => set("assets", v)}
                        placeholder="Drive, Dropbox, Figma, a spreadsheet, existing docs…"
                        hint="Only if it already exists somewhere. You can also send it later."
                        optional
                      />
                      <TextArea
                        label="Anything else we should know?"
                        value={form.notes}
                        onChange={(v) => set("notes", v)}
                        rows={3}
                        placeholder="A deadline you're tied to, a competitor you admire, something that went wrong last time."
                        optional
                      />
                    </>
                  )}
                </div>

                {error && (
                  <p
                    role="alert"
                    className="mt-8 rounded-xl bg-red-50 px-4 py-3 text-[13.5px] leading-relaxed text-red-700"
                  >
                    {error}
                  </p>
                )}

                <div className="mt-10 flex flex-col gap-4 border-t border-neutral-100 pt-7 sm:flex-row-reverse sm:items-center sm:justify-between">
                  {last ? (
                    <button
                      type="submit"
                      disabled={busy}
                      className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-ink px-6 py-3.5 text-[14px] font-medium text-white transition-colors hover:bg-black/85 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {busy ? "Sending…" : "Send it"}
                      {!busy && (
                        <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onNext}
                      className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-ink px-6 py-3.5 text-[14px] font-medium text-white transition-colors hover:bg-black/85"
                    >
                      {step === 0 ? "Continue" : "Next"}
                      <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  )}

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                    {step > 0 && (
                      <button
                        type="button"
                        onClick={() => go(step - 1)}
                        className="text-[13.5px] font-medium text-neutral-500 transition-colors hover:text-ink"
                      >
                        Back
                      </button>
                    )}
                    {/*
                      The escape hatch that makes the rest of the page optional
                      in practice and not just in wording: once we can reply,
                      the brief can be sent from wherever the visitor has got to.
                    */}
                    {!last && canSend && (
                      <button
                        type="button"
                        onClick={() => void submit()}
                        disabled={busy}
                        className="text-[13.5px] font-medium text-neutral-500 underline underline-offset-4 transition-colors hover:text-ink disabled:opacity-60"
                      >
                        {busy ? "Sending…" : "Send what I have"}
                      </button>
                    )}
                  </div>
                </div>

                <p className="mt-6 text-[12.5px] leading-relaxed text-neutral-500">
                  {restored
                    ? "We brought back what you filled in last time. "
                    : "Your answers save in this browser as you type. "}
                  We use them to prepare your proposal and nothing else. See
                  our{" "}
                  <Link
                    to="/privacy"
                    className="underline underline-offset-2 hover:text-ink"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>

              <p className="mt-6 text-[13.5px] text-neutral-500 lg:text-center">
                Would rather just talk?{" "}
                <Link
                  to="/contact"
                  className="text-ink underline underline-offset-4 hover:text-accent"
                >
                  Send us a one-line message
                </Link>{" "}
                or email{" "}
                <a
                  href={`mailto:${SITE.email}`}
                  className="text-ink underline underline-offset-4 hover:text-accent"
                >
                  {SITE.email}
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </Section>
    </>
  )
}

/* ----------------------------------------------------------------------------
   Turning the form into what the API stores.

   Every question travels with its answer, so the dashboard and the
   notification email can render a brief without knowing this file exists.
   Unanswered questions are dropped rather than sent as blanks — on a form this
   optional, most briefs will leave several behind.
---------------------------------------------------------------------------- */
function buildAnswers(f: FormState) {
  const picked = (need: string) => f.needs.includes(need)
  const row = (section: string, label: string, value: string) => ({
    section,
    label,
    value,
  })

  const tone = picked(WEBSITE)
    ? f.tone
        .map((v, i) => {
          const pair = TONE_PAIRS[i]
          if (v === null || !pair) return null
          const [left, right] = pair
          const word = TONE_STEPS[v]!
          const side = v === 2 ? "" : v < 2 ? left : right
          return row(
            "Website & brand",
            `${left} ↔ ${right}`,
            side ? `${word} ${side.toLowerCase()}` : "Balanced",
          )
        })
        .filter((x): x is NonNullable<typeof x> => x !== null)
    : []

  /*
    Each service's block is gated on that service still being selected. Without
    the gate, someone who ticked "Website", chose six pages and then changed
    their mind would send a sitemap along with their automation brief — answers
    to questions the form had already taken back.
  */
  return [
    row("Basics", "Business name", f.business),
    row("Basics", "Phone", f.phone),
    row("Basics", "What they need", f.needs.join(", ")),
    row("Basics", "New or existing", f.stage),
    row("Basics", "What they have today", f.link),
    row("Basics", "What the business does", f.proposition),
    row("Basics", "Timeframe", f.timeframe),
    row("Basics", "Budget range", f.budget),

    row("The project", "Goals", f.goals.join(", ")),

    ...(picked(WEBSITE)
      ? [
          row("Website & brand", "Type of site", f.siteTypes.join(", ")),
          row("Website & brand", "Pages", f.pages.join(", ")),
          row("Website & brand", "Features", f.siteFeatures.join(", ")),
          row("Website & brand", "Dislikes about the current site", f.dislikes),
        ]
      : []),
    ...tone,
    ...(picked(WEBSITE) ? [row("Website & brand", "Brand guidelines", f.brandGuides)] : []),

    ...(picked(SOFTWARE)
      ? [
          row("Software", "What we're building", f.softwareKinds.join(", ")),
          row("Software", "What it needs to do", f.capabilities.join(", ")),
          row("Software", "Systems it must work with", f.systems),
        ]
      : []),

    ...(picked(AUTOMATION)
      ? [
          row("AI & automation", "What takes up the most time", f.manualWork),
          row("AI & automation", "Where that work happens", f.workHappens.join(", ")),
          row("AI & automation", "What to automate", f.automations.join(", ")),
        ]
      : []),

    ...(picked(SUPPORT)
      ? [
          row("Support & growth", "What needs looking after", f.supportNeeds.join(", ")),
          row("Support & growth", "What's going wrong today", f.problems),
        ]
      : []),

    row("The project", "Alongside the project", f.extras.join(", ")),

    row("Details", "Who will use it", f.customers),
    row("Details", "What success looks like", f.success),
    row("Details", "Links shared", f.assets),
    row("Details", "Anything else", f.notes),
  ].filter((a) => a.value.trim())
}

/**
 * The service this brief belongs to in the pipeline.
 *
 * A brief can name several; the lead can only sit in one column, so the first
 * in NEEDS order wins and the full list is in the answers either way.
 */
function leadService(f: FormState) {
  const primary = NEEDS.find((n) => f.needs.includes(n) && LEAD_SERVICE[n])
  return primary ? LEAD_SERVICE[primary]! : ""
}

/** The one line the dashboard list shows before anyone opens the brief. */
function summarise(f: FormState) {
  const head = [
    f.needs.filter((n) => LEAD_SERVICE[n]).join(" + ") || "Project enquiry",
    f.business && `for ${f.business}`,
  ]
    .filter(Boolean)
    .join(" ")
  const tail = f.proposition || f.manualWork || f.problems || f.goals.join(", ")
  return `${head}${tail ? `: ${tail}` : ""}`.slice(0, 400)
}

/** A quiet divider between the blocks one service's questions occupy. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink">
        {children}
      </span>
      <span className="h-px flex-1 bg-neutral-200" />
    </div>
  )
}

/* ----------------------------------------------------------------------------
   Fields
---------------------------------------------------------------------------- */
function Legend({
  children,
  required,
  optional,
}: {
  children: React.ReactNode
  required?: boolean
  optional?: boolean
}) {
  return (
    <span className="text-[12px] font-medium uppercase tracking-[0.14em] text-neutral-500">
      {children}
      {required && <span className="ml-1 text-accent">*</span>}
      {optional && (
        <span className="ml-2 normal-case tracking-normal text-neutral-500">
          optional
        </span>
      )}
    </span>
  )
}

function ErrorText({ children }: { children?: string }) {
  if (!children) return null
  return (
    <p role="alert" className="mt-2 text-[12.5px] text-red-600">
      {children}
    </p>
  )
}

const inputCls =
  "mt-2 w-full rounded-xl border bg-white px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-neutral-400"

function TextField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  hint,
  error,
  required,
  optional,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  hint?: string
  error?: string
  required?: boolean
  optional?: boolean
}) {
  return (
    <label className="block">
      <Legend required={required} optional={optional}>
        {label}
      </Legend>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        className={`${inputCls} ${
          error
            ? "border-red-300 focus:border-red-500"
            : "border-neutral-300 focus:border-ink"
        }`}
      />
      {hint && !error && (
        <p className="mt-1.5 text-[12.5px] text-neutral-500">{hint}</p>
      )}
      <ErrorText>{error}</ErrorText>
    </label>
  )
}

function TextArea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
  optional,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
  optional?: boolean
}) {
  return (
    <label className="block">
      <Legend optional={optional}>{label}</Legend>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${inputCls} resize-none border-neutral-300 leading-relaxed focus:border-ink`}
      />
    </label>
  )
}

function OptionGroup({
  legend,
  hint,
  options,
  value,
  onChange,
  optional,
  inline = false,
}: {
  legend: string
  hint?: string
  options: readonly string[]
  value: string
  onChange: (v: string) => void
  optional?: boolean
  inline?: boolean
}) {
  return (
    <fieldset>
      <legend>
        <Legend optional={optional}>{legend}</Legend>
      </legend>
      {hint && <p className="mt-1.5 text-[13px] text-neutral-500">{hint}</p>}
      <div
        className={
          inline
            ? "mt-3 flex flex-wrap gap-2.5"
            : "mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2"
        }
      >
        {options.map((o) => {
          const active = value === o
          return (
            <button
              key={o}
              type="button"
              aria-pressed={active}
              /* Tapping the chosen option again clears it — an optional
                 question a visitor answered by accident must be answerable
                 back to nothing. */
              onClick={() => onChange(active ? "" : o)}
              className={`rounded-full border px-4 py-2.5 text-[13.5px] font-medium transition-colors ${
                active
                  ? "border-ink bg-ink text-white"
                  : "border-neutral-300 text-neutral-600 hover:border-ink hover:text-ink"
              }`}
            >
              {o}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

/**
 * Multi-select chips.
 *
 * Long lists start collapsed: the six most common answers are a glance, and
 * the rest are one tap away. Anything already selected stays visible whether
 * the list is expanded or not, so collapsing can never hide an answer.
 */
function ChipGroup({
  legend,
  hint,
  options,
  selected,
  onToggle,
  initial,
}: {
  legend: string
  hint?: string
  options: readonly string[]
  selected: string[]
  onToggle: (v: string) => void
  initial?: number
}) {
  const [expanded, setExpanded] = useState(false)
  const collapsed = initial !== undefined && !expanded && options.length > initial
  const visible = collapsed
    ? options.filter((o, i) => i < initial || selected.includes(o))
    : options

  return (
    <fieldset>
      <legend>
        <Legend optional>{legend}</Legend>
      </legend>
      {hint && <p className="mt-1.5 text-[13px] text-neutral-500">{hint}</p>}
      <div className="mt-3 flex flex-wrap gap-2.5">
        {visible.map((o) => {
          const active = selected.includes(o)
          return (
            <button
              key={o}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(o)}
              className={`rounded-full border px-4 py-2 text-[13px] font-medium transition-colors ${
                active
                  ? "border-ink bg-ink text-white"
                  : "border-neutral-300 text-neutral-600 hover:border-ink hover:text-ink"
              }`}
            >
              {o}
            </button>
          )
        })}
        {collapsed && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-full border border-dashed border-neutral-300 px-4 py-2 text-[13px] font-medium text-neutral-500 transition-colors hover:border-ink hover:text-ink"
          >
            + {options.length - initial} more
          </button>
        )}
      </div>
    </fieldset>
  )
}

/** A five-point position between two opposing words. */
function TonePicker({
  left,
  right,
  value,
  onChange,
}: {
  left: string
  right: string
  value: number | null
  onChange: (v: number | null) => void
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
      <span
        className={`text-[13.5px] sm:w-28 sm:text-right ${
          value !== null && value < 2 ? "font-semibold text-ink" : "text-neutral-500"
        }`}
      >
        {left}
      </span>
      <div className="flex flex-1 items-center gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => {
          const active = value === i
          return (
            <button
              key={i}
              type="button"
              aria-label={`${left} to ${right}: ${i + 1} of 5`}
              aria-pressed={active}
              onClick={() => onChange(active ? null : i)}
              className={`h-9 flex-1 rounded-lg border transition-colors ${
                active
                  ? "border-ink bg-ink"
                  : "border-neutral-200 bg-neutral-50 hover:border-neutral-400"
              }`}
            />
          )
        })}
      </div>
      <span
        className={`text-[13.5px] sm:w-28 ${
          value !== null && value > 2 ? "font-semibold text-ink" : "text-neutral-500"
        }`}
      >
        {right}
      </span>
    </div>
  )
}
